import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Permiso, Usuario } from "../../shared/types";
import { api, ErrorApi, establecerAreaGlobal } from "../lib/apiClient";

const CLAVE_AREA_SELECCIONADA = "jvd_area_seleccionada";

interface ContextoAuth {
  usuario: Usuario | null;
  permisos: Set<Permiso>;
  tenantSlug: string | null;
  cargandoInicial: boolean;
  areaSeleccionada: string | null;
  setAreaSeleccionada: (id: string | null) => void;
  // El estudio ya usa Áreas de Práctica y este usuario no tiene ninguna
  // asignada — ver lib/areas-practica.ts (calcularAreasVisibles).
  sinAreasAsignadas: boolean;
  iniciarSesion: (
    slug: string,
    email: string,
    password: string,
  ) => Promise<void>;
  cerrarSesion: () => Promise<void>;
  recargarUsuario: () => Promise<void>;
}

const ContextoAuthReact = createContext<ContextoAuth | null>(null);

export function ProveedorAuth({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [permisos, setPermisos] = useState<Set<Permiso>>(new Set());
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [cargandoInicial, setCargandoInicial] = useState(true);
  const [sinAreasAsignadas, setSinAreasAsignadas] = useState(false);
  const [areaSeleccionada, setAreaSeleccionadaState] = useState<string | null>(() => {
    const guardada = localStorage.getItem(CLAVE_AREA_SELECCIONADA);
    establecerAreaGlobal(guardada);
    return guardada;
  });

  const setAreaSeleccionada = useCallback((id: string | null) => {
    setAreaSeleccionadaState(id);
    establecerAreaGlobal(id);
    if (id) localStorage.setItem(CLAVE_AREA_SELECCIONADA, id);
    else localStorage.removeItem(CLAVE_AREA_SELECCIONADA);
  }, []);

  const recargarUsuario = useCallback(async () => {
    const datos = await api.get<{
      usuario: Usuario;
      permisos: Permiso[];
      tenantSlug: string | null;
      sinAreasAsignadas: boolean;
    }>("/auth/me");
    setUsuario(datos.usuario);
    setPermisos(new Set(datos.permisos));
    setTenantSlug(datos.tenantSlug);
    setSinAreasAsignadas(datos.sinAreasAsignadas);
  }, []);

  useEffect(() => {
    recargarUsuario()
      .catch(() => {
        setUsuario(null);
        setPermisos(new Set());
        setTenantSlug(null);
        setSinAreasAsignadas(false);
      })
      .finally(() => setCargandoInicial(false));
  }, [recargarUsuario]);

  const iniciarSesion = useCallback(
    async (slug: string, email: string, password: string) => {
      const datos = await api.post<{ usuario: Usuario }>("/auth/login", {
        slug,
        email,
        password,
      });
      setUsuario(datos.usuario);
      await recargarUsuario();
    },
    [recargarUsuario],
  );

  const cerrarSesion = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      if (!(error instanceof ErrorApi)) throw error;
    }
    setUsuario(null);
    setPermisos(new Set());
    setTenantSlug(null);
    setSinAreasAsignadas(false);
    setAreaSeleccionada(null);
  }, [setAreaSeleccionada]);

  return (
    <ContextoAuthReact.Provider
      value={{
        usuario,
        permisos,
        tenantSlug,
        cargandoInicial,
        areaSeleccionada,
        setAreaSeleccionada,
        sinAreasAsignadas,
        iniciarSesion,
        cerrarSesion,
        recargarUsuario,
      }}
    >
      {children}
    </ContextoAuthReact.Provider>
  );
}

export function useAuth(): ContextoAuth {
  const contexto = useContext(ContextoAuthReact);
  if (!contexto) throw new Error("useAuth debe usarse dentro de ProveedorAuth");
  return contexto;
}
